import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { updateUserStatus } from '../../../api/user/update-user-status'
import {
  UserStatusEnum,
  userStatusReasonMapping
} from '../../../lib/types/enums/users/user-status.enum'
import { Staff } from '../../../lib/types/models/staff/staff'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

type Account = Pick<Staff['user'], 'status' | 'statusReason'>

// The label of the staff space status, the reason comes from the backend
const getStaffAccountStatusLabel = ({ status, statusReason }: Account) => {
  if (status === UserStatusEnum.ACTIVE) return 'Actif'
  return statusReason ? userStatusReasonMapping[statusReason] : 'Désactivé'
}

// The staff space status badge, for the staff list column
export const StaffAccountStatusBadge = ({ account }: { account?: Account }) => {
  if (!account?.status) return '-'
  return (
    <Badge
      variant={account.status === UserStatusEnum.ACTIVE ? 'success' : 'danger'}
      text={getStaffAccountStatusLabel(account)}
    />
  )
}

type Props = {
  account?: Staff['user']
  onReactivated?: () => void
}

// Status of the staff space (active / deactivated and why), when it happened, and the button to reactivate it
const StaffAccountStatus = ({ account, onReactivated }: Props) => {
  const { mutate: reactivate, isPending } = useMutation({
    mutationFn: updateUserStatus,
    onSuccess: () => {
      toast.success("L'espace encadrant a été réactivé")
      onReactivated?.()
    },
    onError: showAxiosError
  })

  if (!account?.status) return null

  const isDeactivated = account.status !== UserStatusEnum.ACTIVE

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
        Espace encadrant :
      </Typography>
      <StaffAccountStatusBadge account={account} />
      {isDeactivated && account.statusUpdatedAt && (
        <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
          {`depuis le ${DateFormatter.DDMMYYYY(account.statusUpdatedAt)}`}
        </Typography>
      )}
      {account.canReactivate && (
        <Button
          type="button"
          variant="ghost"
          size="medium"
          label="Réactiver l'espace encadrant"
          loading={isPending}
          disabled={isPending}
          onClick={() => reactivate({ id: account.id, status: UserStatusEnum.ACTIVE })}
        />
      )}
    </div>
  )
}

export default StaffAccountStatus
